/**
 * Regras do bloco de conteúdo (Conteúdos Visuais / Modo Edição B2B).
 *
 * Vivem aqui, fora dos componentes, porque decidem o que o painel grava: o contrato
 * enviado ao `SiteLogoRequest` do verso-api, quais campos aparecem no form e o que é
 * obrigatório em cada tipo. Errar qualquer uma delas grava um bloco que o B2B não
 * renderiza — e nada estoura no caminho.
 */

/** Slots da home que aceitam bloco de conteúdo (as demais chaves são conteúdo legado). */
export const CHAVES_SLOT_HOME = [
  "pre_vitrine",
  "entre_vitrine",
  "pos_vitrine",
  "pre_rodape"
];

/** Tipos de conteúdo do slot da home (contrato novo) e do tipo segurança (legado). */
export const CONTEUDO_TIPOS_SLOT_HOME = ["imagem", "texto"];
export const CONTEUDO_TIPOS_LEGADO = ["HTML", "IMAGEM"];

/** Aparência com que um bloco novo nasce — e o piso de um registro antigo, sem estilo. */
export const ESTILO_PADRAO = {
  fundo_tipo: "nenhum",
  cor_fundo: "#f1f3f5",
  cor_fundo_final: "#dee2e6",
  posicao_imagem: "fundo",
  alinhamento_texto: "esquerda",
  tamanho_texto: "medio",
  cta_texto: null,
  cta_cor_fundo: "#dc3545",
  cta_cor_texto: "#ffffff",
  cantos_arredondados: true,
  usa_borda: false,
  cor_borda: "#dee2e6",
  espessura_borda: 1,
  largura_total: false
};

const clonar = (valor) => JSON.parse(JSON.stringify(valor));

export const ehTipoSeguranca = (item = {}) =>
  item.tipo === "seguranca" || item.chave === "seguranca";

export const ehTipoSlotHome = (item = {}) =>
  CHAVES_SLOT_HOME.includes(item.chave);

export const ehBlocoTexto = (item = {}) =>
  ehTipoSlotHome(item) && item.conteudo_tipo === "texto";

export const ehConteudoHtml = (item = {}) => item.conteudo_tipo === "HTML";

/**
 * O form mostra os campos de imagem?
 *
 * Bloco de texto não tem imagem, e o tipo segurança em modo HTML edita o conteúdo no
 * editor — em nenhum dos dois a imagem faria sentido.
 */
export const exibeCamposImagem = (item = {}) => {
  if (ehTipoSeguranca(item) && ehConteudoHtml(item)) {
    return false;
  }

  return !ehBlocoTexto(item);
};

/**
 * Imagem desktop e mobile são obrigatórias?
 *
 * Espelha o `SiteLogoRequest`: as duas são exigidas no bloco de imagem e no legado
 * `IMAGEM`. Sem esta regra no form, o operador só descobria pelo erro da API depois de
 * preencher tudo — e, num conteúdo antigo (cadastrado antes da coluna mobile existir),
 * o salvamento falhava sem que nada na tela indicasse o motivo.
 */
export const imagensObrigatorias = (item = {}) => exibeCamposImagem(item);

/** O título é o que identifica o bloco de texto: sem ele não há o que renderizar. */
export const tituloObrigatorio = (item = {}) => ehBlocoTexto(item);

/**
 * `conteudo_tipo` válido para o tipo escolhido.
 *
 * Dois contratos convivem: o tipo segurança é legado (`HTML`/`IMAGEM`) e os slots da
 * home usam `imagem`/`texto`. Um valor do contrato errado faz o B2B não renderizar o
 * bloco, então o valor é corrigido ao trocar o tipo, em vez de viajar como está.
 */
export const conteudoTipoNormalizado = (item = {}) => {
  if (ehTipoSeguranca(item)) {
    return CONTEUDO_TIPOS_LEGADO.includes(item.conteudo_tipo)
      ? item.conteudo_tipo
      : "IMAGEM";
  }

  if (ehTipoSlotHome(item)) {
    return CONTEUDO_TIPOS_SLOT_HOME.includes(item.conteudo_tipo)
      ? item.conteudo_tipo
      : "imagem";
  }

  return item.conteudo_tipo;
};

/**
 * Item pronto para o form, com o estilo completo.
 *
 * `estilo` é nulo em tudo que foi cadastrado antes do Modo Edição, e o form assume o
 * objeto inteiro: sem a mescla com os padrões, abrir um registro antigo quebrava a tela.
 * Só o que veio preenchido sobrepõe o padrão — `null` do backend não apaga o default.
 */
export const normalizarItemParaEdicao = (item = {}) => {
  const copia = clonar(item);
  const estilo = clonar(ESTILO_PADRAO);
  const recebido =
    copia.estilo && typeof copia.estilo === "object" ? copia.estilo : {};

  Object.keys(recebido).forEach((chave) => {
    if (recebido[chave] !== null && recebido[chave] !== undefined) {
      estilo[chave] = recebido[chave];
    }
  });

  estilo.cantos_arredondados = !!estilo.cantos_arredondados;
  estilo.usa_borda = !!estilo.usa_borda;
  estilo.largura_total = !!estilo.largura_total;
  estilo.espessura_borda = Number(estilo.espessura_borda) || 1;

  copia.estilo = estilo;

  return copia;
};

/**
 * Payload do que vai para a API.
 *
 * O texto do botão vira `null` quando está em branco: gravado como string vazia, o B2B
 * renderiza um botão sem rótulo em vez de não renderizar botão nenhum.
 */
export const montarPayload = (item = {}) => {
  const payload = clonar(item);

  if (payload.estilo && typeof payload.estilo === "object") {
    const texto = payload.estilo.cta_texto;

    payload.estilo.cta_texto =
      texto && String(texto).trim() !== "" ? String(texto).trim() : null;
  }

  return payload;
};

/** Item novo: um bloco de imagem de largura inteira, sem estilo aplicado. */
export const itemNovo = () => ({
  id: null,
  imagem: null,
  imagem_mobile: null,
  descricao: null,
  ordem: 1,
  ativo: 1,
  link: null,
  chave: null,
  tipo_id: 1,
  largura: 12,
  largura_tablet: null,
  largura_mobile: null,
  conteudo_tipo: "imagem",
  titulo: null,
  subtitulo: null,
  resumo: null,
  imagem_fundo: null,
  estilo: clonar(ESTILO_PADRAO),
  acesso_site: 0,
  acesso_app: 0,
  acesso_cliente_final: 0
});
